/*
 * Primitive types.
 */

export type ChangeHandler = ChangeAsyncHandler | ChangeCallbackHandler | ChangeSyncHandler;

/** Handler that returns a promise that resolves when it is done making changes. */
export type ChangeAsyncHandler = () => Promise<void>;
/** Handler that calls a callback when it is done making changes. */
export type ChangeCallbackHandler = (handler: () => void) => void;
/** Handler that makes changes synchronously. */
export type ChangeSyncHandler = () => void;

export type ChangeManagerSubscriber = (isDirty: boolean) => void;

/*
 * Interface.
 */

/** Helps separate app-induced changes from UI/user-induced changes, kind of inspired by NgModelController. */
export interface ChangeManager {
  /** Indicates the user has made changes in the UI. */
  readonly isDirty: boolean;
  /** Don't mark as dirty while making changes. */
  ignoreChanges(changeHandler?: ChangeHandler): Promise<void>;
  /** Mark as pristine, and don't mark as dirty while resetting to a pristine state. */
  markAsPristine(resetHandler?: ChangeHandler): void;
  /** Mark as dirty. */
  markAsDirty(): void;
  /** Start listening for changes. Unsubscribes the previous subscriber, if any. */
  subscribe: (subscriber: ChangeManagerSubscriber) => void;
  /** Stop listening for changes and dispose of underlying resources. */
  unsubscribe(): void;
}

/*
 * Helpers.
 */

export function isChangeCallbackHandler(handler: ChangeHandler): handler is ChangeCallbackHandler {
  return handler.length === 1;
}
